import React, { useState, useEffect } from 'react';
import { Activity, Phone, MapPin, Clock, Leaf, Heart, Users, Shield, Star, Menu, X } from 'lucide-react';

const MitaliHomeoWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ 
      behavior: 'smooth' 
    });
    setIsMenuOpen(false);
  };

  const FloatingElement = ({ children, delay = 0 }) => (
    <div 
      className={`floating-element`}
      style={{
        animationDelay: `${delay}s`,
        animation: 'float 6s ease-in-out infinite'
      }}
    >
      {children}
    </div>
  );

  const ServiceCard = ({ icon: Icon, title, description, delay = 0 }) => (
    <div 
      className={`service-card ${visibleSections.has('services') ? 'animate-in' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="service-icon">
        <Icon size={48} />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );

  const ContactCard = ({ icon: Icon, title, content }) => (
    <div className="contact-card">
      <div className="contact-icon">
        <Icon size={32} />
      </div>
      <h3>{title}</h3>
      <div className="contact-content">{content}</div>
    </div>
  );

  return (
    <div className="mitali-homeo-website">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Georgia', serif;
          line-height: 1.6;
          color: #2c3e50;
        }

        .mitali-homeo-website {
          min-height: 100vh;
        }

        /* Header Styles */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: ${scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)'};
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          box-shadow: ${scrolled ? '0 2px 30px rgba(0,0,0,0.15)' : '0 2px 20px rgba(0,0,0,0.1)'};
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          display: flex;
          align-items: center;
          font-size: 1.8rem;
          font-weight: bold;
          color: #2c3e50;
          cursor: pointer;
        }

        .logo-icon {
          margin-right: 10px;
          color: #667eea;
        }

        .nav-links {
          display: flex;
          list-style: none;
          gap: 2rem;
        }

        .nav-link {
          color: #2c3e50;
          text-decoration: none;
          font-weight: 500;
          padding: 0.5rem 0;
          position: relative;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .nav-link:hover {
          color: #667eea;
          transform: translateY(-2px);
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          transition: width 0.3s ease;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: #2c3e50;
          cursor: pointer;
        }

        .mobile-menu {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          padding: 1rem 0;
        }

        .mobile-menu.open {
          display: block;
        }

        .mobile-nav-link {
          display: block;
          padding: 1rem 20px;
          color: #2c3e50;
          text-decoration: none;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
        }

        .mobile-nav-link:hover {
          background: #f8f9fa;
          color: #667eea;
        }

        /* Hero Section */
        .hero {
          height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.4);
          z-index: 1;
        }

        .floating-element {
          position: absolute;
          color: rgba(255,255,255,0.1);
          font-size: 3rem;
          z-index: 1;
        }

        .floating-element:nth-child(1) { top: 15%; left: 10%; }
        .floating-element:nth-child(2) { top: 60%; right: 15%; }
        .floating-element:nth-child(3) { bottom: 25%; left: 20%; }
        .floating-element:nth-child(4) { top: 30%; right: 25%; }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
          padding: 0 20px;
          animation: fadeInUp 1s ease;
        }

        .hero h1 {
          font-size: 3.5rem;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .hero p {
          font-size: 1.3rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        .cta-button {
          display: inline-block;
          padding: 15px 30px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          color: white;
          text-decoration: none;
          border-radius: 50px;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          cursor: pointer;
          border: none;
          font-size: 1rem;
        }

        .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.4);
        }

        /* Section Styles */
        .section {
          padding: 5rem 0;
        }

        .section:nth-child(even) {
          background: #f8f9fa;
        }

        .section:nth-child(odd) {
          background: white;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-header h2 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: #2c3e50;
          position: relative;
        }

        .section-header h2::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 3px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          border-radius: 2px;
        }

        .section-header p {
          font-size: 1.1rem;
          color: #666;
        }

        /* About Section */
        .about-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
        }

        .about-text {
          font-size: 1.1rem;
          line-height: 1.8;
        }

        .about-text h3 {
          color: #2c3e50;
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }

        .about-text p {
          margin-bottom: 1.5rem;
          color: #555;
        }

        .about-image {
          height: 400px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 6rem;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          position: relative;
          overflow: hidden;
        }

        .about-image::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(102,126,234,0.3), rgba(118,75,162,0.3));
        }

        /* Services Grid */
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .service-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          text-align: center;
          transition: all 0.3s ease;
          opacity: 0;
          transform: translateY(30px);
        }

        .service-card.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .service-icon {
          color: #667eea;
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }

        .service-card h3 {
          margin-bottom: 1rem;
          color: #2c3e50;
          font-size: 1.3rem;
        }

        .service-card p {
          color: #666;
          line-height: 1.6;
        }

        /* Contact Section */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .contact-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          text-align: center;
          transition: all 0.3s ease;
        }

        .contact-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0,0,0,0.15);
        }

        .contact-icon {
          color: #667eea;
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }

        .contact-card h3 {
          margin-bottom: 1rem;
          color: #2c3e50;
          font-size: 1.3rem;
        }

        .contact-content {
          color: #555;
          line-height: 1.8;
        }

        /* Footer */
        .footer {
          background: #2c3e50;
          color: white;
          text-align: center;
          padding: 2rem 0;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .nav-links {
            display: none;
          }

          .mobile-menu-btn {
            display: block;
          }

          .hero h1 {
            font-size: 2.5rem;
          }

          .hero p {
            font-size: 1.1rem;
          }

          .about-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .services-grid {
            grid-template-columns: 1fr;
          }

          .section-header h2 {
            font-size: 2rem;
          }

          .floating-element {
            font-size: 2rem;
          }
        }

        @media (max-width: 480px) {
          .hero h1 {
            font-size: 2rem;
          }

          .section {
            padding: 3rem 0;
          }

          .service-card,
          .contact-card {
            padding: 1.5rem;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <div className="nav-container">
          <div className="logo" onClick={() => scrollToSection('home')}>
            <Activity className="logo-icon" size={32} />
            Mitali Homeo Hall
          </div>

          <nav className="nav-links">
            <span className="nav-link" onClick={() => scrollToSection('home')}>Home</span>
            <span className="nav-link" onClick={() => scrollToSection('about')}>About</span>
            <span className="nav-link" onClick={() => scrollToSection('services')}>Services</span>
            <span className="nav-link" onClick={() => scrollToSection('contact')}>Contact</span>
          </nav>

          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <span className="mobile-nav-link" onClick={() => scrollToSection('home')}>Home</span>
            <span className="mobile-nav-link" onClick={() => scrollToSection('about')}>About</span>
            <span className="mobile-nav-link" onClick={() => scrollToSection('services')}>Services</span>
            <span className="mobile-nav-link" onClick={() => scrollToSection('contact')}>Contact</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <FloatingElement delay={0}><Leaf /></FloatingElement>
        <FloatingElement delay={2}><Heart /></FloatingElement>
        <FloatingElement delay={4}><Shield /></FloatingElement>
        <FloatingElement delay={6}><Star /></FloatingElement>

        <div className="hero-content">
          <h1>Traditional Homeopathy</h1>
          <p>Discover the power of natural healing with our time-tested homeopathic remedies and expert guidance</p>
          <button className="cta-button" onClick={() => scrollToSection('contact')}>
            Consult Our Experts
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <div className="container">
          <div className="section-header">
            <h2>About Our Practice</h2>
            <p>Generations of healing wisdom at your service</p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <h3>Dr. Sanjit Kr Das, Expert Homeopathic Care with over 40+ years in Homeopathic medical treatment</h3>
              <p>With decades of experience in traditional homeopathy, our practitioners combine classical knowledge with modern understanding to provide personalized healing solutions.</p>
              
              <p>Our extensive collection of authentic remedies, carefully prepared and stored in traditional wooden cabinets, ensures the highest quality and potency for every treatment.</p>
              
              <p>We believe in treating the whole person, not just symptoms, following the fundamental principles of homeopathy established by Dr. Samuel Hahnemann partnered with Hahnemann.</p>
            </div>
            <div className="about-image">
              <Activity size={100} />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive homeopathic care for your wellbeing</p>
          </div>
          <div className="services-grid">
            <ServiceCard 
              icon={Leaf}
              title="Constitutional Treatment"
              description="Personalized remedies based on your unique constitution and symptoms for long-lasting health improvements."
              delay={100}
            />
            <ServiceCard 
              icon={Heart}
              title="Acute Care"
              description="Quick relief for immediate health concerns with carefully selected homeopathic remedies."
              delay={200}
            />
            <ServiceCard 
              icon={Users}
              title="Pediatric Care"
              description="Gentle, safe homeopathic treatments specially formulated for infants and children."
              delay={300}
            />
            <ServiceCard 
              icon={Shield}
              title="Chronic Conditions"
              description="Specialized treatment plans for managing chronic health conditions naturally and effectively."
              delay={400}
            />
            <ServiceCard 
              icon={Activity}
              title="Herbal Remedies"
              description="Traditional herbal preparations and plant-based medicines for holistic healing."
              delay={500}
            />
            <ServiceCard 
              icon={Star}
              title="Health Consultation"
              description="Comprehensive health assessments and personalized treatment recommendations."
              delay={600}
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <div className="section-header">
            <h2>Visit Our Store</h2>
            <p>Experience traditional homeopathy with modern convenience</p>
          </div>
          <div className="contact-grid">
            <ContactCard 
              icon={MapPin}
              title="Location"
              content={
                <div>
                  Mitali Homeo Hall<br/>
                  Near UCO Bank Building<br/>
                  Maligaon Chariali, Guwahati - 781011
                </div>
              }
            />
            <ContactCard 
              icon={Phone}
              title="Phone"
              content={
                <div>
                  +91-9864103717<br/>
                  +91-8638553950<br/>
                  Available: 9 AM - 9 PM (IST)
                </div>
              }
            />
            <ContactCard 
              icon={Clock}
              title="Store Hours"
              content={
                <div>
                  Monday - Sunday<br/>
                  10:00 AM - 9:00 PM (IST)<br/>
                  Saturday: 10:00 AM - 2:00 PM (IST)<br/>
                  Special Hours: 5:00 PM - 8:00 PM (IST)
                </div>
              }
            />
            <ContactCard 
              icon={Activity}
              title="Services"
              content={
                <div>
                  Consultation Available<br/>
                  Home Remedies<br/>
                  Traditional Medicines
                </div>
              }
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Mitali Homeo Hall. Dedicated to your natural health and wellbeing.</p>
        </div>
      </footer>
    </div>
  );
};

export default MitaliHomeoWebsite;